import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronDown, Heart, MessageCircle, Package, Phone, Ruler, Search, Shirt, ShoppingBag, User, X, Bookmark } from 'lucide-react';
import { DEPARTMENTS, COLLECTIONS } from './Data/taxonomy';
import { AuthContext } from './Firebase/AuthProvider';
import markImg from './assets/measurments/mark-m.svg';
import wordmark from './assets/download.svg';
import { useShowroom } from './Business/showroom';
import { ShowroomSwitch } from './Business/BusinessOrderBook';
import './navslider.css';

const WHATSAPP = '918871081395';

// Design your own: the customizer for each garment family.
const DESIGN = [
  { label: 'Suit & blazer', to: '/custom/suit/select_cloth/personalize' },
  { label: 'Kurta set', to: '/custom/ethnic/select_cloth/personalize' },
  { label: 'Kurta only', to: '/custom/only_kurta/select_cloth/personalize' },
  { label: 'Sadri', to: '/custom/sadri/select_cloth/personalize' },
  { label: 'Send us a design photo', to: '/send-your-design' },
];

// Departments, each opening to its main style pages (from the shared
// taxonomy). Uniforms are B2B and sit under For business.
const MENU_STYLES = 6;
const shopDepartments = DEPARTMENTS.filter((d) => !d.b2b).map((d) => ({
  slug: d.slug,
  label: d.label,
  short: d.short,
  styles: d.styles
    .filter((s) => s.page && s.priority <= 2)
    .sort((a, b) => a.priority - b.priority)
    .slice(0, MENU_STYLES),
}));

const MORE = [
  { title: 'Wedding outfit planner', path: '/wedding-outfit-planner' },
  { title: 'Size chart', path: '/size-chart' },
  { title: 'Journal', path: '/blog' },
  { title: 'About us', path: '/about' },
  { title: 'Contact us', path: '/reachus/general' },
];

const BUSINESS = [
  { title: 'Business uniforms', path: '/clothing-for-business' },
  { title: 'Get a quotation', path: '/reachus/getQuotation' },
  { title: 'Smart kiosk franchise', path: '/smart-kiosk-franchise' },
  { title: 'Campus suit stalls', path: '/campus-suit-stalls' },
];

const ACCOUNT = [
  { title: 'Orders', path: '/profile/orders', icon: Package },
  { title: 'Saved designs', path: '/profile/saved-designs', icon: Bookmark },
  { title: 'Measurements', path: '/profile/measurements', icon: Ruler },
  { title: 'Wishlist', path: '/profile/wishlist', icon: Heart },
];

const bagCount = () => {
  try {
    const list = JSON.parse(localStorage.getItem('sel') || '[]');
    return Array.isArray(list) ? list.length : 0;
  } catch (e) {
    return 0;
  }
};

const nameOf = (user) => {
  const n = user?.displayName || user?.email?.split('@')[0] || '';
  return n ? n.trim().toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : 'Your account';
};

function Group({ id, label, open, setOpen, children, current }) {
  const isOpen = open === id;
  return (
    <div className={`sn-group ${isOpen ? 'is-open' : ''} ${current ? 'is-current' : ''}`}>
      <button type="button" className="sn-row sn-row--head" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : id)}>
        <span>{label}</span>
        <ChevronDown size={17} aria-hidden="true" className="sn-chev" />
      </button>
      <ul className="sn-sub" hidden={!isOpen}>{children}</ul>
    </div>
  );
}

export default function SidebarNav({ sidebar, setSideBar }) {
  const { user } = useContext(AuthContext);
  const { isBiz, showroom } = useShowroom();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [q, setQ] = useState('');
  const [bag, setBag] = useState(0);
  const searchRef = useRef(null);
  const close = () => setSideBar(false);

  // Open the department of the page we are on.
  const currentDept = pathname.startsWith('/collections/')
    ? 'occasion'
    : pathname.startsWith('/custom/')
      ? 'design'
      : (pathname.match(/^\/products\/([^/]+)/) || [])[1] || null;
  const [open, setOpen] = useState(currentDept);

  useEffect(() => {
    if (!sidebar) return undefined;
    setBag(bagCount());
    setOpen(currentDept);
    // No page scroll behind the open menu; Escape closes it.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [sidebar]);

  const go = (path) => {
    navigate(path);
    close();
  };
  const onSearch = (e) => {
    e.preventDefault();
    const term = q.trim();
    go(term ? `/products?q=${encodeURIComponent(term)}` : '/products');
    setQ('');
  };
  const isAt = (path) => (path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(`${path}/`));
  const help = `https://wa.me/${WHATSAPP}?text=${encodeURIComponent('Hi Maviinci, I need some help.')}`;

  return (
    <nav
      className={`nav_sidebar ${sidebar ? 'nav_sidebar_open' : 'nav_sidebar_close'} sidebar-container sn`}
      aria-label="Menu"
      aria-hidden={!sidebar}
    >
      <div className="sn-head">
        <Link to="/" className="sn-brand" onClick={close} aria-label="Maviinci home">
          <span className="sn-brand__mark" style={{ '--mark': `url(${markImg})` }} aria-hidden="true" />
          <img className="sn-brand__name" src={wordmark} alt="Maviinci" />
        </Link>
        <button type="button" className="sn-close" onClick={close} aria-label="Close menu">
          <X size={20} aria-hidden="true" />
        </button>
      </div>

      <form className="sn-search" onSubmit={onSearch} role="search">
        <Search size={17} aria-hidden="true" />
        <input
          ref={searchRef}
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search: groom sherwani, office suit…"
          aria-label="Search the studio"
          tabIndex={sidebar ? 0 : -1}
        />
        <button type="submit" aria-label="Search" tabIndex={sidebar ? 0 : -1}><ArrowRight size={16} aria-hidden="true" /></button>
      </form>

      <div className="sn-scroll">
        <Link to="/products" onClick={close} className={`sn-feature ${isAt('/products') && !currentDept ? 'is-current' : ''}`}>
          <span>
            <small>The studio</small>
            <strong>Shop every look</strong>
          </span>
          <ArrowRight size={18} aria-hidden="true" />
        </Link>

        <p className="sn-label">Shop</p>
        {shopDepartments.map((d) => (
          <Group key={d.slug} id={d.slug} label={d.label} open={open} setOpen={setOpen} current={currentDept === d.slug}>
            <li><Link to={`/products/${d.slug}`} onClick={close} className="sn-all">All {d.short}</Link></li>
            {d.styles.map((st) => (
              <li key={st.slug}>
                <Link to={`/products/${d.slug}/${st.slug}`} onClick={close} className={isAt(`/products/${d.slug}/${st.slug}`) ? 'is-current' : ''}>{st.label}</Link>
              </li>
            ))}
          </Group>
        ))}
        <Group id="occasion" label="Shop by occasion" open={open} setOpen={setOpen} current={currentDept === 'occasion'}>
          {COLLECTIONS.map((c) => (
            <li key={c.slug}>
              <Link to={`/collections/${c.slug}`} onClick={close} className={isAt(`/collections/${c.slug}`) ? 'is-current' : ''}>{c.label}</Link>
            </li>
          ))}
        </Group>

        <p className="sn-label">Made to measure</p>
        <Group id="design" label="Design your own" open={open} setOpen={setOpen} current={currentDept === 'design'}>
          {DESIGN.map((x) => (
            <li key={x.to}><Link to={x.to} onClick={close}>{x.label}</Link></li>
          ))}
        </Group>
        {MORE.map((x) => (
          <Link key={x.path} to={x.path} onClick={close} className={`sn-row ${isAt(x.path) ? 'is-current' : ''}`}>{x.title}</Link>
        ))}

        <p className="sn-label">For business</p>
        {BUSINESS.map((x) => (
          <Link key={x.path} to={x.path} onClick={close} className={`sn-row ${isAt(x.path) ? 'is-current' : ''}`}>{x.title}</Link>
        ))}

        {user && (
          <>
            <p className="sn-label">Your account</p>
            {/* A boutique switches its prices here at the counter; back to costs asks for its PIN. */}
            {isBiz && (
              <div className="sn-showroom">
                <span><b>Showroom</b><small>{showroom ? 'Client prices on this device' : 'Your costs on this device'}</small></span>
                <ShowroomSwitch compact />
              </div>
            )}
            <div className="sn-account">
              {ACCOUNT.map(({ title, path, icon: Icon }) => (
                <Link key={path} to={path} onClick={close} className={isAt(path) ? 'is-current' : ''}>
                  <Icon size={18} aria-hidden="true" />{title}
                </Link>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="sn-foot">
        <div className="sn-foot__row">
          <button type="button" className="sn-me" onClick={() => go(user ? '/profile' : '/login')}>
            <span className="sn-me__icon">{user ? nameOf(user).charAt(0) : <User size={17} aria-hidden="true" />}</span>
            <span className="sn-me__text">
              <strong>{user ? nameOf(user) : 'Log in'}</strong>
              <small>{user ? 'Your profile' : 'Save designs and measurements'}</small>
            </span>
          </button>
          <button type="button" className="sn-bag" onClick={() => go('/cart')} aria-label={`Bag, ${bag} item${bag === 1 ? '' : 's'}`}>
            <ShoppingBag size={20} aria-hidden="true" />
            {bag > 0 && <b>{bag}</b>}
          </button>
        </div>
        <div className="sn-help">
          <a href={help} target="_blank" rel="noopener noreferrer"><MessageCircle size={16} aria-hidden="true" />WhatsApp a tailor</a>
          <a href="/reachus/general" onClick={(e) => { e.preventDefault(); go('/reachus/general'); }}><Phone size={16} aria-hidden="true" />Call us</a>
          <button type="button" className="sn-help__design" onClick={() => { setOpen('design'); setTimeout(() => document.querySelector('.sn-group.is-open')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50); }}>
            <Shirt size={16} aria-hidden="true" />Design your own
          </button>
        </div>
      </div>
    </nav>
  );
}
